/**
 * Rôle issu du JWT (session persistée sans objet `user` après F5 / avant fetchProfile).
 */
export function decodeJwtRole(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { role?: unknown };
    if (typeof payload.role === 'string' && payload.role.length > 0) {
      return payload.role.toLowerCase().trim();
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Aligne les variantes backend / legacy pour les contrôles d’accès. */
export function normalizeAuthRole(role: string | null | undefined): string | null {
  if (role == null || role === '') return null;
  const r = String(role).toLowerCase().trim();
  if (r === 'institution') return 'institution_admin';
  if (r === 'collaborator') return 'collaborateur';
  return r;
}

/** Rôles autorisés sur l'espace apprenant (/learner). */
export const LEARNER_WORKSPACE_ROLES = [
  'learner',
  'student',
  'teacher',
  'resp_formation',
  'collaborateur',
  'collaborator',
  'admin',
  'institution_admin',
  'institution',
  'manager',
] as const;

/** Rôles avec accès au tableau de bord formateur (en plus de l'espace apprenant). */
export const TRAINER_DASHBOARD_ROLES = [
  'teacher',
  'resp_formation',
  'collaborateur',
  'collaborator',
  'admin',
  'institution_admin',
  'institution',
  'manager',
] as const;

export function canAccessLearnerWorkspace(role: string | null | undefined): boolean {
  return roleMatchesAllowlist(role, LEARNER_WORKSPACE_ROLES);
}

export function canAccessTrainerDashboard(role: string | null | undefined): boolean {
  return roleMatchesAllowlist(role, TRAINER_DASHBOARD_ROLES);
}

/** Apprenant LyrraOne pur (pas encore formateur). */
export function isLyrraOnePureLearner(role: string | null | undefined): boolean {
  const n = normalizeAuthRole(role);
  return n === 'student' || n === 'learner';
}

export function canUpgradeToLyrraOneTrainer(role: string | null | undefined): boolean {
  return isLyrraOnePureLearner(role);
}

/** Lien principal du menu utilisateur LyrraOne (header public / store). */
export function getLyrraOneUserDashboardHref(role: string | null | undefined): string {
  return canAccessTrainerDashboard(role) ? '/dashboard' : '/learner';
}

export function canAccessAdminPanel(role: string | null | undefined): boolean {
  return normalizeAuthRole(role) === 'admin';
}

/** Vérifie si le rôle utilisateur correspond à une des entrées (chacune normalisée). */
export function roleMatchesAllowlist(role: string | null | undefined, allowed: readonly string[]): boolean {
  const n = normalizeAuthRole(role);
  if (!n) return false;
  return allowed.some((a) => normalizeAuthRole(a) === n);
}
